import React, { useEffect, useRef, useState } from "react";
import { animated, useSpring } from "react-spring-new";
import { intl } from "LocaleWrapper";

import { Table } from "@cofano/cck";
import { Tabs, Tab, Box } from "@mui/material";

import { DataFormat, Language } from "../../imports/Utils";

import { useFrontendTable } from "components/table";
import { FormattedDate } from "utils/DateUtils";

import UsageIndicator from "./UsageIndicator";
import "./logblock.scss";

const VoyageLog = ({ report }) => {

	const [ selectedTab, selectTab ] = useState(1);

	const { tableProps } = useFrontendTable({
		id: "portVisits",
		defaultSize: "COMPACT",
		defaultPageSize: 3,
		content: report?.visitedPorts || [],
		paginationEnabled: report?.visitedPorts?.length > 3,
		paginationSizeOptions: [ 3 ],
		saveForOrganizationEnabled: false,
	});

	return (
		<div style={{ minHeight: 160 }}>
			<Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }}>
				<Tabs value={selectedTab} onChange={(event, newValue) => selectTab(newValue)}>
					<Tab label={Language("SUMMARY")} value={1} />
					<Tab label={Language("PORT_VISITS")} value={2} />
				</Tabs>
			</Box>

			{selectedTab === 1 && (
				<div className="log-block-summary" style={{ overflow: "hidden" }}>
					{report && <div className="log-block-summary-distance">
						<div className="log-summary-title">{Language("DISTANCE")}</div>
						<DistanceIndicator report={report} />
					</div>}
					{report && <div className="log-block-summary-usage">
						<div className="log-summary-title">{Language("USAGE_AMOUNT")}</div>
						<UsageIndicator
							report={report}
							dossier={true} />
					</div>}
					{report && <div className="log-block-summary-duration">
						<div className="log-summary-title">{Language("DURATION")}</div>
						<DurationIndicator report={report} />
					</div>}
					<span className="log-block-summary-legend">
						{Language("ACTUAL")}
						{Language("ESTIMATED")}
					</span>
				</div>
			)}

			{selectedTab === 2 && (
				<Table
					{...tableProps}
					columns={[ {
						id: "location.description",
						title: intl.formatMessage({ id: "LOCATION" }),
					}, {
						id: "enteredDateTime",
						title: intl.formatMessage({ id: "SHIP_LOG_ENTERED_DATETIME" }),
						render: row => <FormattedDate date={row.enteredDateTime} />,
					}, {
						id: "exitedDateTime",
						title: intl.formatMessage({ id: "SHIP_LOG_EXITED_DATETIME" }),
						render: row => <FormattedDate date={row.exitedDateTime} />,
					} ]} />
			)}
		</div>
	);
};

const DistanceIndicator = ({ report }) => {

	const estimatedSailingDistance = report.estimatedSailingDistanceInKm || report.streamDistanceInKm;
	const container = useRef();
	const [ containerWidth, setContainerWidth ] = useState(0);
	const { estimateWidth, actualWidth } = calculateWidths(report.estimatedSailingDistanceInKm, report.actualSailingDistanceInKm, containerWidth);
	const [ rendered, setRendered ] = useState(false);

	const expected = useSpring({
		from: { number: 0, width: 0 },
		number: estimatedSailingDistance || 0,
		width: estimateWidth,
		delay: !rendered ? 800 : 0,
	});

	const current = useSpring({
		from: { number: 0, width: 0 },
		number: report.actualSailingDistanceInKm,
		width: actualWidth,
		delay: !rendered ? 1200 : 0,
		onRest: () => setRendered(true),
	});

	const setWidth = () => {
		setContainerWidth(container.current?.offsetWidth);
	};

	useEffect(() => {
		setWidth();

		window.addEventListener("resize", setWidth);

		return () => window.removeEventListener("resize", setWidth);
	}, []);

	return <div className="log-block-summary-distance-expected">
		<div ref={container} className="log-block-expected-distance-bar-container" >
			<animated.div style={current} className="log-block-distance-current">
				<div>
					<animated.span>{report.actualSailingDistanceInKm ? current.number.to(n => DataFormat.distance(n.toFixed(0))) : "?"}</animated.span>
				</div>
			</animated.div>
			<animated.div style={expected} className="log-block-distance-expected">
				<div>
					<animated.span>{estimatedSailingDistance ? expected.number.to(n => DataFormat.distance(n.toFixed(0))) : "?"}</animated.span>
				</div>
			</animated.div>
		</div>
	</div>;
};

export const DurationIndicator = ({ report }) => {

	const estimatedSailingMinutes = report.estimatedSailingMinutes;
	const actualSailingMinutes = report.actualSailingMinutes;

	const currentHours = useSpring({
		from: { number: 0 },
		to: { number: estimatedSailingMinutes ? Math.floor(estimatedSailingMinutes / 60) : 0 },
		delay: 1000,
	});

	const currentMinutes = useSpring({
		from: { number: 0 },
		to: { number: estimatedSailingMinutes ? estimatedSailingMinutes % 60 : 0 },
		delay: 1400,
	});

	const expectedHours = useSpring({
		from: { number: 0 },
		to: { number: actualSailingMinutes ? Math.floor(actualSailingMinutes / 60) : 0 },
		delay: 1000,
	});

	const expectedMinutes = useSpring({
		from: { number: 0 },
		to: { number: actualSailingMinutes ? actualSailingMinutes % 60 : 0 },
		delay: 1400,
	});

	return <div className="duration-container">
		<div className="duration-current">
			<animated.span className="duration-hours">{actualSailingMinutes ? expectedHours.number.to(n => n.toFixed(0) + "h") : "?h"}</animated.span>
			<animated.span className="duration-minutes">{actualSailingMinutes ? expectedMinutes.number.to(n => n.toFixed(0) + "m") : "?m"}</animated.span>
		</div>
		<div className="duration-expected">
			<animated.span className="duration-hours">{estimatedSailingMinutes ? currentHours.number.to(n => n.toFixed(0) + "h") : "?h"}</animated.span>
			<animated.span className="duration-minutes">{estimatedSailingMinutes ? currentMinutes.number.to(n => n.toFixed(0) + "m") : "?m"}</animated.span>
		</div>
	</div>;
};

const calculateWidths = (estimatedSailingDistance, actualSailingDistance, containerWidth = 200) => {
	const MAX = containerWidth;
	const MIN = 10; //looks cleaner
	const clamp = (val) => val > MIN ? val : MIN;

	if (!actualSailingDistance || !estimatedSailingDistance) {
		return { estimateWidth: MAX, actualWidth: MIN };
	}
	if (actualSailingDistance > estimatedSailingDistance) {
		return { estimateWidth: clamp(MAX * (estimatedSailingDistance / actualSailingDistance)), actualWidth: MAX };
	}
	if (estimatedSailingDistance > actualSailingDistance) {
		return { estimateWidth: MAX, actualWidth: clamp(MAX * (actualSailingDistance / estimatedSailingDistance)) };
	}
};

export default VoyageLog;
